import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Action, Connector, Trigger } from '../tasks/types';
import React, { useEffect, useState } from 'react';
import TextWithArrows from '../common/TextWithArrows.tsx';
import { base64SvgToSrc, stringReplace } from '../common/utils';
import { ExInput } from '@boomi/exosphere';
import AnimatedLink from '../common/AnimatedLink';
import ItemList from '../common/ItemList';

import './new-task-suggestions.css';

type Suggestion = {
    trigger: Trigger;
    action: Action;
};

const NewTaskSuggestions = () => {
    const { language, connectors } = useApp();
    const navigate = useNavigate();

    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState('');

    const filteredSuggestions = suggestions.filter(
        (item) =>
            item.trigger.name.toUpperCase().includes(search.toUpperCase()) ||
            item.action.name.toUpperCase().includes(search.toUpperCase()),
    );

    useEffect(() => {
        // This fixes the related test. Not sure why the test falls over otherwise?!
        if (!Object.values(connectors).length) return;

        const selectedConnectorsAsTriggers: Connector[] = searchParams
            .getAll('trigger')
            .map((connectorId) => connectors[connectorId]);

        const selectedConnectorsAsActions: Connector[] = searchParams
            .getAll('action')
            .map((connectorId) => connectors[connectorId]);

        const triggers: Trigger[] = [];
        const actions: Action[] = [];

        for (const connector of selectedConnectorsAsTriggers) {
            for (const trigger of Object.values(connector.triggers)) {
                triggers.push(trigger);
            }
        }

        for (const connector of selectedConnectorsAsActions) {
            for (const action of Object.values(connector.actions)) {
                actions.push(action);
            }
        }

        const triggerActionCombinations: Suggestion[] = triggers.flatMap((trigger) =>
            actions.map((action) => ({ trigger, action })),
        );

        setSuggestions(triggerActionCombinations);
    }, [connectors, searchParams]);

    const onCreateTaskFromSuggestion = (suggestion: Suggestion) => {
        const params = new URLSearchParams({
            'trigger-connector': suggestion.trigger.connectorId,
            'trigger-event': suggestion.trigger.id,
            'action-connector': suggestion.action.connectorId,
            'action-event': suggestion.action.id,
        });
        navigate(`/tasks/new-task/?` + params);
    };

    const generateSuggestionDescription = (
        triggerConnectorId: string,
        actionConnectorId: string,
    ) => {
        if (triggerConnectorId === actionConnectorId) {
            return stringReplace(
                translations[language].SUGGESTIONS_description_self,
                connectors[triggerConnectorId].displayName,
            );
        }

        return stringReplace(
            translations[language].SUGGESTIONS_description,
            connectors[triggerConnectorId].displayName,
            connectors[actionConnectorId].displayName,
        );
    };

    return (
        <ItemList
            backTo="/tasks/new"
            title={translations[language].SUGGESTIONS_title}
            hint={translations[language].SUGGESTIONS_hint}
            toolbarFilters={
                <ExInput
                    data-testId="suggestionsSearchInput"
                    className="suggestion-search exo-search-input"
                    placeholder={translations[language].COMMON_search_placeholder}
                    input-id="default"
                    type="text"
                    footertype="info"
                    inputmode="text"
                    size="default"
                    flavor="default"
                    leadingicon="magnifying-glass"
                    removemarginbottom
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearch(e.currentTarget.value)
                    }
                />
            }
            items={filteredSuggestions}
            itemContent={(suggestion) => (
                <button
                    className="suggestion-layout ta-tile ta-tile-interactive"
                    key={`${suggestion.trigger.name}_${suggestion.action.name}`}
                    onClick={() => onCreateTaskFromSuggestion(suggestion)}
                >
                    <div className="suggestion-icons-wrapper">
                        <img
                            className="suggestion-icon"
                            src={base64SvgToSrc(connectors[suggestion.trigger.connectorId].icon)}
                            alt={suggestion.trigger.connectorId}
                        />
                        <img
                            className="suggestion-icon"
                            src={base64SvgToSrc(connectors[suggestion.action.connectorId].icon)}
                            alt={suggestion.action.connectorId}
                        />
                    </div>
                    <div className="suggestion-content-wrapper">
                        <span className="suggestion-name title lg">
                            <TextWithArrows
                                text={`${suggestion.trigger.name} -> ${suggestion.action.name}`}
                            />
                        </span>
                        <span className="text sm">
                            {generateSuggestionDescription(
                                suggestion.trigger.connectorId,
                                suggestion.action.connectorId,
                            )}
                        </span>
                    </div>
                </button>
            )}
            extraContent={
                <p className="text md">
                    {translations[language].ONBOARDING_footer_1}
                    <AnimatedLink className="btn-text" to="/tasks/new-task">
                        {translations[language].ONBOARDING_new_task}
                    </AnimatedLink>
                    {translations[language].ONBOARDING_footer_2}.
                </p>
            }
            emptyStateText={translations[language].COMMON_search_hint}
        />
    );
};

export default NewTaskSuggestions;
